/**
 * 접근성 (aria-label) 전용 번역 (#410)
 *
 * 시각 라벨 i18n 통합 후 잔존한 한국어 aria-label 을 시정하기 위한 광역 a11y 사전.
 * - Toast / LoginPromptSheet 단순 닫기
 * - MenuQuantityControl 수량 +/-
 * - QrSourceBanner 3개 source 분기 안내
 * - PaymentResultPage 4개 결과별 nav 라벨
 *
 * 박제 패턴 (메가 박제 신규 후보):
 * 시각 라벨 i18n 통합 후 aria-label 누락 — i18n migration 시 *시각* 텍스트는
 * 정합되었으나 *접근성* 라벨 한국어 잔존 (스크린 리더 사용자에 한국어 노출).
 *
 * 단순 *닫기* / *수량 ±* → 시각 라벨 직접 재사용 가능하나, 시각 라벨이 부재한
 * 경우가 많아 (icon-only 버튼) 별도 a11y 사전 분리.
 *
 * es-MX 멕시코 표준 어휘 + 악센트 정합.
 */
import type { Language, Locale } from '@/config/locales';

export interface A11yTranslation {
  /** Toast 알림 닫기 (icon-only 버튼) */
  closeNotification: string;
  /** 일반 닫기 (LoginPromptSheet 등 모달의 X 버튼) */
  close: string;
  /** MenuQuantityControl 감소 버튼 */
  decreaseQuantity: string;
  /** MenuQuantityControl 증가 버튼 */
  increaseQuantity: string;
  /** QrSourceBanner — qr-table + 동일 매장 (테이블 오더 컨텍스트 유지) */
  qrTableContextMaintained: string;
  /** QrSourceBanner — qr-table + 다른 매장 (픽업 주문으로 안내) */
  qrPickupFallbackForOtherStore: string;
  /** QrSourceBanner — qr-pickup (픽업 매장 위치 기준 안내) */
  qrPickupLocationGuide: string;
  /** PaymentResultPage — 결제 완료 후 동작 nav */
  paymentResultCompleteActions: string;
  /** PaymentResultPage — 결제 대기 중 동작 nav */
  paymentResultPendingActions: string;
  /** PaymentResultPage — 결제 취소 후 동작 nav */
  paymentResultCancelledActions: string;
  /** PaymentResultPage — 결제 실패 후 동작 nav */
  paymentResultFailedActions: string;
}

// #512 — Locale 단일 출처 정합 (translations.ts 패턴 준수).
export type A11yTranslations =
  & Record<Language, A11yTranslation>
  & Record<Exclude<Locale, Language>, A11yTranslation>;

export const a11yTranslations: A11yTranslations = {
  ko: {
    closeNotification: '알림 닫기',
    close: '닫기',
    decreaseQuantity: '수량 감소',
    increaseQuantity: '수량 증가',
    qrTableContextMaintained: '테이블 오더 컨텍스트 유지',
    qrPickupFallbackForOtherStore: '다른 매장 — 픽업 주문으로 안내',
    qrPickupLocationGuide: '픽업 매장 위치 기준 안내',
    paymentResultCompleteActions: '결제 완료 후 동작',
    paymentResultPendingActions: '결제 대기 중 동작',
    paymentResultCancelledActions: '결제 취소 후 동작',
    paymentResultFailedActions: '결제 실패 후 동작',
  },
  es: {
    closeNotification: 'Cerrar notificación',
    close: 'Cerrar',
    decreaseQuantity: 'Disminuir cantidad',
    increaseQuantity: 'Aumentar cantidad',
    qrTableContextMaintained: 'Mantener contexto de orden de mesa',
    qrPickupFallbackForOtherStore: 'Otra tienda — guía a orden para recoger',
    qrPickupLocationGuide: 'Guía basada en ubicación de tienda con pedidos para recoger',
    paymentResultCompleteActions: 'Acción después del pago completado',
    paymentResultPendingActions: 'Acción durante el pago pendiente',
    paymentResultCancelledActions: 'Acción después del pago cancelado',
    paymentResultFailedActions: 'Acción después del pago fallido',
  },
  en: {
    closeNotification: 'Close notification',
    close: 'Close',
    decreaseQuantity: 'Decrease quantity',
    increaseQuantity: 'Increase quantity',
    qrTableContextMaintained: 'Table order context maintained',
    qrPickupFallbackForOtherStore: 'Different store — guided to pickup order',
    qrPickupLocationGuide: 'Guide based on pickup store location',
    paymentResultCompleteActions: 'Actions after payment completed',
    paymentResultPendingActions: 'Actions during payment pending',
    paymentResultCancelledActions: 'Actions after payment cancelled',
    paymentResultFailedActions: 'Actions after payment failed',
  },
  pt: {
    closeNotification: 'Fechar notificação',
    close: 'Fechar',
    decreaseQuantity: 'Diminuir quantidade',
    increaseQuantity: 'Aumentar quantidade',
    qrTableContextMaintained: 'Contexto de pedido de mesa mantido',
    qrPickupFallbackForOtherStore: 'Loja diferente — orientação para pedido de retirada',
    qrPickupLocationGuide: 'Guia baseado na localização da loja de retirada',
    paymentResultCompleteActions: 'Ações após pagamento concluído',
    paymentResultPendingActions: 'Ações durante pagamento pendente',
    paymentResultCancelledActions: 'Ações após pagamento cancelado',
    paymentResultFailedActions: 'Ações após pagamento falhado',
  },
  fr: {
    closeNotification: 'Fermer la notification',
    close: 'Fermer',
    decreaseQuantity: 'Diminuer la quantité',
    increaseQuantity: 'Augmenter la quantité',
    qrTableContextMaintained: 'Contexte de commande de table maintenu',
    qrPickupFallbackForOtherStore: 'Magasin différent — guidé vers la commande à emporter',
    qrPickupLocationGuide: 'Guide basé sur l’emplacement du magasin de retrait',
    paymentResultCompleteActions: 'Actions après le paiement terminé',
    paymentResultPendingActions: 'Actions pendant le paiement en attente',
    paymentResultCancelledActions: 'Actions après l’annulation du paiement',
    paymentResultFailedActions: 'Actions après l’échec du paiement',
  },
  zh: {
    closeNotification: '关闭通知',
    close: '关闭',
    decreaseQuantity: '减少数量',
    increaseQuantity: '增加数量',
    qrTableContextMaintained: '保持桌面订单上下文',
    qrPickupFallbackForOtherStore: '其他门店 — 引导至自取订单',
    qrPickupLocationGuide: '基于自取门店位置的指南',
    paymentResultCompleteActions: '支付完成后的操作',
    paymentResultPendingActions: '支付待处理时的操作',
    paymentResultCancelledActions: '支付取消后的操作',
    paymentResultFailedActions: '支付失败后的操作',
  },
  ja: {
    closeNotification: '通知を閉じる',
    close: '閉じる',
    decreaseQuantity: '数量を減らす',
    increaseQuantity: '数量を増やす',
    qrTableContextMaintained: 'テーブル注文コンテキスト維持',
    qrPickupFallbackForOtherStore: '別の店舗 — 受取注文へ案内',
    qrPickupLocationGuide: '受取店舗の位置に基づく案内',
    paymentResultCompleteActions: '支払い完了後の操作',
    paymentResultPendingActions: '支払い保留中の操作',
    paymentResultCancelledActions: '支払いキャンセル後の操作',
    paymentResultFailedActions: '支払い失敗後の操作',
  },
  hi: {
    closeNotification: 'सूचना बंद करें',
    close: 'बंद करें',
    decreaseQuantity: 'मात्रा घटाएं',
    increaseQuantity: 'मात्रा बढ़ाएं',
    qrTableContextMaintained: 'टेबल ऑर्डर संदर्भ बनाए रखा',
    qrPickupFallbackForOtherStore: 'अलग स्टोर — पिकअप ऑर्डर के लिए मार्गदर्शन',
    qrPickupLocationGuide: 'पिकअप स्टोर स्थान के आधार पर मार्गदर्शन',
    paymentResultCompleteActions: 'भुगतान पूरा होने के बाद की कार्रवाई',
    paymentResultPendingActions: 'भुगतान लंबित होने के दौरान की कार्रवाई',
    paymentResultCancelledActions: 'भुगतान रद्द होने के बाद की कार्रवाई',
    paymentResultFailedActions: 'भुगतान विफल होने के बाद की कार्रवाई',
  },
  de: {
    closeNotification: 'Benachrichtigung schließen',
    close: 'Schließen',
    decreaseQuantity: 'Menge verringern',
    increaseQuantity: 'Menge erhöhen',
    qrTableContextMaintained: 'Tischbestellungskontext beibehalten',
    qrPickupFallbackForOtherStore: 'Anderes Geschäft — zur Abholbestellung weitergeleitet',
    qrPickupLocationGuide: 'Anleitung basierend auf dem Standort des Abholgeschäfts',
    paymentResultCompleteActions: 'Aktionen nach abgeschlossener Zahlung',
    paymentResultPendingActions: 'Aktionen bei ausstehender Zahlung',
    paymentResultCancelledActions: 'Aktionen nach abgebrochener Zahlung',
    paymentResultFailedActions: 'Aktionen nach fehlgeschlagener Zahlung',
  },
  it: {
    closeNotification: 'Chiudi notifica',
    close: 'Chiudi',
    decreaseQuantity: 'Diminuisci quantità',
    increaseQuantity: 'Aumenta quantità',
    qrTableContextMaintained: 'Contesto ordine tavolo mantenuto',
    qrPickupFallbackForOtherStore: 'Negozio diverso — guidato all’ordine da ritirare',
    qrPickupLocationGuide: 'Guida basata sulla posizione del negozio di ritiro',
    paymentResultCompleteActions: 'Azioni dopo il pagamento completato',
    paymentResultPendingActions: 'Azioni durante il pagamento in sospeso',
    paymentResultCancelledActions: 'Azioni dopo il pagamento annullato',
    paymentResultFailedActions: 'Azioni dopo il pagamento fallito',
  },
  ru: {
    closeNotification: 'Закрыть уведомление',
    close: 'Закрыть',
    decreaseQuantity: 'Уменьшить количество',
    increaseQuantity: 'Увеличить количество',
    qrTableContextMaintained: 'Контекст заказа за столиком сохранён',
    qrPickupFallbackForOtherStore: 'Другой магазин — направлено к заказу на самовывоз',
    qrPickupLocationGuide: 'Руководство на основе местоположения магазина самовывоза',
    paymentResultCompleteActions: 'Действия после завершения оплаты',
    paymentResultPendingActions: 'Действия во время ожидания оплаты',
    paymentResultCancelledActions: 'Действия после отмены оплаты',
    paymentResultFailedActions: 'Действия после неудачной оплаты',
  },
  ar: {
    closeNotification: 'إغلاق الإشعار',
    close: 'إغلاق',
    decreaseQuantity: 'تقليل الكمية',
    increaseQuantity: 'زيادة الكمية',
    qrTableContextMaintained: 'الحفاظ على سياق طلب الطاولة',
    qrPickupFallbackForOtherStore: 'متجر مختلف — التوجيه إلى طلب الاستلام',
    qrPickupLocationGuide: 'دليل بناءً على موقع متجر الاستلام',
    paymentResultCompleteActions: 'الإجراءات بعد اكتمال الدفع',
    paymentResultPendingActions: 'الإجراءات أثناء انتظار الدفع',
    paymentResultCancelledActions: 'الإجراءات بعد إلغاء الدفع',
    paymentResultFailedActions: 'الإجراءات بعد فشل الدفع',
  },
  vi: {
    closeNotification: 'Đóng thông báo',
    close: 'Đóng',
    decreaseQuantity: 'Giảm số lượng',
    increaseQuantity: 'Tăng số lượng',
    qrTableContextMaintained: 'Duy trì ngữ cảnh đơn hàng tại bàn',
    qrPickupFallbackForOtherStore: 'Cửa hàng khác — hướng dẫn đến đơn hàng lấy tại quầy',
    qrPickupLocationGuide: 'Hướng dẫn dựa trên vị trí cửa hàng lấy hàng',
    paymentResultCompleteActions: 'Hành động sau khi thanh toán hoàn tất',
    paymentResultPendingActions: 'Hành động trong khi thanh toán đang chờ',
    paymentResultCancelledActions: 'Hành động sau khi thanh toán bị huỷ',
    paymentResultFailedActions: 'Hành động sau khi thanh toán thất bại',
  },
  th: {
    closeNotification: 'ปิดการแจ้งเตือน',
    close: 'ปิด',
    decreaseQuantity: 'ลดจำนวน',
    increaseQuantity: 'เพิ่มจำนวน',
    qrTableContextMaintained: 'รักษาบริบทคำสั่งซื้อโต๊ะ',
    qrPickupFallbackForOtherStore: 'ร้านอื่น — แนะนำไปยังคำสั่งซื้อรับเอง',
    qrPickupLocationGuide: 'คู่มือตามตำแหน่งร้านรับสินค้า',
    paymentResultCompleteActions: 'การกระทำหลังชำระเงินสำเร็จ',
    paymentResultPendingActions: 'การกระทำระหว่างรอชำระเงิน',
    paymentResultCancelledActions: 'การกระทำหลังยกเลิกการชำระเงิน',
    paymentResultFailedActions: 'การกระทำหลังชำระเงินล้มเหลว',
  },
  nl: {
    closeNotification: 'Melding sluiten',
    close: 'Sluiten',
    decreaseQuantity: 'Aantal verlagen',
    increaseQuantity: 'Aantal verhogen',
    qrTableContextMaintained: 'Tafelbestelling context behouden',
    qrPickupFallbackForOtherStore: 'Andere winkel — doorverwezen naar afhaalbestelling',
    qrPickupLocationGuide: 'Gids op basis van locatie afhaalwinkel',
    paymentResultCompleteActions: 'Acties na voltooide betaling',
    paymentResultPendingActions: 'Acties tijdens openstaande betaling',
    paymentResultCancelledActions: 'Acties na geannuleerde betaling',
    paymentResultFailedActions: 'Acties na mislukte betaling',
  },
};

/**
 * 로케일에 따른 a11y 번역 가져오기
 */
export function getA11yTranslation(locale: Locale): A11yTranslation {
  return a11yTranslations[locale];
}
